import type { ReactNode } from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export type ActiveTool = "chat" | "audit" | "debate" | "comparison" | null;

const TITLES: Record<NonNullable<ActiveTool>, string> = {
  chat: "Ask the corpus",
  audit: "Coverage Audit",
  debate: "Field Debate",
  comparison: "Paper Comparison",
};

export interface ActivePanelStaleness {
  /** Integer 0–100 — Jaccard overlap between snapshot and current scope. */
  overlapPercent: number;
  onRerun: () => void;
  isPending?: boolean;
  /** Override the default "Rerun on current scope" label. */
  rerunLabel?: string;
  /** Optional second line (e.g. the FD heavy-drift "consider Rebuild" nudge). */
  advisory?: string;
}

interface Props {
  activeTool: ActiveTool;
  onClose: () => void;
  /** Rendered when no tool is active (the AnalyzeOverview). */
  idle: ReactNode;
  headerActions?: ReactNode;
  /** When set, render an amber STALE strip below the header with a Rerun affordance. */
  staleness?: ActivePanelStaleness | null;
  children?: ReactNode;
}

export function AnalyzeActivePanel({
  activeTool,
  onClose,
  idle,
  headerActions,
  staleness,
  children,
}: Props) {
  if (activeTool === null) {
    return <div className="h-full overflow-hidden rounded-md border">{idle}</div>;
  }
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-md border">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <h2 className="text-sm font-semibold">{TITLES[activeTool]}</h2>
        <div className="flex items-center gap-1">
          {headerActions}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close tool"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {staleness && (
        <div
          role="status"
          aria-live="polite"
          data-testid="analyze-stale-banner"
          className="flex items-center justify-between gap-2 border-b border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <AlertTriangle className="size-3.5 shrink-0" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate">
                Result reflects an older paper set ({staleness.overlapPercent}% overlap).
              </span>
              {staleness.advisory && (
                <span className="truncate text-amber-200/80">{staleness.advisory}</span>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={staleness.onRerun}
            disabled={staleness.isPending}
            className="h-6 px-2 text-amber-100 hover:bg-amber-500/15 hover:text-amber-100"
            aria-label="Rerun analysis on current scope"
          >
            <RefreshCw
              className={`size-3 mr-1 ${staleness.isPending ? "animate-spin" : ""}`}
            />
            {staleness.rerunLabel ?? "Rerun on current scope"}
          </Button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}
